import { Form, Formik, FormikHelpers } from "formik";
import { useId, useMemo, useState } from "react";
import * as Yup from "yup";
import { Button } from "@volca/ui/components/ui/button";
import { Card } from "@volca/ui/components/ui/card";
import {
  PageContainer,
  PageHeader,
} from "@volca/ui/components/ui/page-components";
import { Stack } from "@volca/ui/components/ui/stack";
import { toast } from "@volca/ui/lib/toast";
import { IBusiness } from "@/business/business";
import { useSaveBusiness } from "@/business/query";
import { SETTINGS_PAGE_TITLES } from "@/business-settings/constants";
import { useBusiness } from "@/core/business";
import BusinessPaymentsSettings from "./business-payments-settings";
import { BusinessServiceTitanSettings } from "./business-service-titan-settings";
// import BusinessReferralUpdateMessages from "./BusinessReferralUpdateMessages";
import BusinessSummary from "./business-summary";
import { GoogleBusinessConnection } from "./google-business-connection";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  phone_number: Yup.string().required("Phone number is required"),
});

const Business = () => {
  const { selectedBusiness, businessId } = useBusiness();

  const [isEditMode, setIsEditMode] = useState(false);
  const formId = useId();

  const initialValues = useMemo<Partial<IBusiness>>(
    () => ({
      name: selectedBusiness?.name,
      credit_payment_enabled: selectedBusiness?.credit_payment_enabled,
      max_payout: selectedBusiness?.max_payout,
      phone_number: selectedBusiness?.phone_number,
      service_titan_enabled: selectedBusiness?.service_titan_enabled,
    }),
    [selectedBusiness],
  );

  const { mutate: saveBusiness } = useSaveBusiness(businessId);

  const handleSubmit = async (
    values: Partial<IBusiness>,
    { resetForm }: FormikHelpers<Partial<IBusiness>>,
  ) =>
    saveBusiness(values, {
      onSuccess: () => {
        resetForm({ values });
        toast.success("Business updated successfully");
        setIsEditMode(false);
      },
      onError: (error) => {
        toast.error("Failed to update business", {
          description:
            error instanceof Error ? error.message : "Please try again",
        });
      },
    });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ dirty, resetForm, isSubmitting }) => (
        <PageContainer>
          <PageHeader
            title={SETTINGS_PAGE_TITLES[""]}
            actions={
              !isEditMode ? (
                <Button
                  type="button"
                  onClick={() => setIsEditMode(true)}
                  variant="secondary"
                >
                  Edit
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    onClick={() => {
                      setIsEditMode(false);
                      resetForm();
                    }}
                    variant="secondary"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  {/* In the header, outside the form, so it names the form. */}
                  <Button
                    type="submit"
                    form={formId}
                    disabled={!dirty || isSubmitting}
                    loading={isSubmitting}
                  >
                    Save
                  </Button>
                </>
              )
            }
          />
          {/* A form: narrower than the frame, aligned left. */}
          <Card className="w-full max-w-3xl gap-0 py-0">
            <Form id={formId}>
              <Stack align="start" gap={0} className="w-full gap-12 p-6">
                {!!selectedBusiness && (
                  <>
                    <BusinessSummary business={selectedBusiness} />
                    <BusinessPaymentsSettings isEditMode={isEditMode} />
                    <BusinessServiceTitanSettings
                      isEditMode={isEditMode}
                      setIsEditMode={setIsEditMode}
                    />
                    <GoogleBusinessConnection business={selectedBusiness} />
                    {/* TODO: send isEditMode to BusinessReferralUpdateMessages when ready */}
                    {/* <BusinessReferralUpdateMessages /> */}
                  </>
                )}
              </Stack>
            </Form>
          </Card>
        </PageContainer>
      )}
    </Formik>
  );
};

export default Business;
